// Logique pure de l'onglet « Passation » (DECISIONS §37) : ordre des participant·e·s, gestes de correction, compteurs, textes.
// Sans React ni réseau, donc testée (accueil.test.ts). Les mots des signaux viennent du serveur (server/utils/passationContenu.ts, SIGNAL).
import type { ParticipantPassation } from '../../types';
import { dePersonne } from '../messages/format';
import { queNom } from '../demande/nouvelle/textesDemande';

/** Ce que « Rien à signaler » écrit dans le champ : un vrai texte, que la remplaçante lit comme les autres. */
export const RIEN_DE_PARTICULIER = 'Rien de particulier.';

/** Les personnes qui débutent d'abord (c'est l'information la plus importante), puis l'ordre alphabétique. */
export function trierParticipants<T extends { nom: string; signaux: string[] }>(ps: T[]): T[] {
  return [...ps].sort((a, b) => (b.signaux.length > 0 ? 1 : 0) - (a.signaux.length > 0 ? 1 : 0) || a.nom.localeCompare(b.nom, 'fr'));
}

export const qui_debute = (ps: ParticipantPassation[]): ParticipantPassation[] => trierParticipants(ps.filter((p) => p.signaux.length > 0));
export const autres_participants = (ps: ParticipantPassation[]): ParticipantPassation[] => trierParticipants(ps.filter((p) => p.signaux.length === 0));

/** Le geste de correction proposé sur la ligne d'une personne qui débute (null : aucun). `valeur` = ce qui est envoyé au serveur (null : retour à Eveil). */
export interface GesteCorrection { libelle: string; aria: string; valeur: boolean | null }

export function gesteCorrection(p: ParticipantPassation): GesteCorrection | null {
  // L'intervenant·e a ajouté la personne : elle peut retirer son ajout (retour à ce que dit Eveil).
  if (p.premiere_fois === true) return { libelle: '✕ Retirer', aria: `Retirer l’indication « débute » pour ${p.nom}`, valeur: null };
  // Elle a corrigé « ne débute pas ici » mais la personne reste dans la liste à cause de sa mesure : on peut annuler la correction.
  if (p.premiere_fois === false) return { libelle: 'Annuler la correction', aria: `Annuler la correction pour ${p.nom}`, valeur: null };
  // Eveil signale une séance dans l'atelier (1re, 2e ou « sans doute ») : « Ne débute pas ici » la retire.
  if (p.signal_atelier) return { libelle: '✕ Ne débute pas ici', aria: `${p.nom} ne débute pas dans l’atelier`, valeur: false };
  return null;
}

/** Les personnes retirées de la liste par l'intervenant·e (« ne débute pas ici ») et qui n'y figurent plus : on garde un moyen d'annuler. */
export const retirees = (ps: ParticipantPassation[]): ParticipantPassation[] => trierParticipants(ps.filter((p) => p.premiere_fois === false && p.signaux.length === 0));

/** Un signal affiché : son mot, s'il est incertain, et qui l'a indiqué quand c'est l'intervenant·e (jamais un mot technique). */
export interface SignalAffiche { texte: string; doute: boolean; indiquePar: string | null }

/** Qui a posé la correction : « vous » si c'est la personne qui regarde, sinon le prénom de l'auteur (l'absent·e ou la gestion à sa place). */
export function auteurCorrection(p: ParticipantPassation, parDefaut: string): string {
  return p.premiere_fois_par_vous ? 'vous' : prenom(p.premiere_fois_par_nom) || parDefaut;
}

export function signauxAffiches(p: ParticipantPassation, parDefaut: string): SignalAffiche[] {
  return p.signaux.map((texte) => ({
    texte,
    doute: texte.startsWith('peut-être'),
    // « 1re séance dans l'atelier » n'est « indiqué par » que lorsque la correction de l'intervenant·e l'a posé (et que ce n'est pas Eveil qui le dit).
    indiquePar: p.premiere_fois === true && texte.startsWith('1re séance dans') ? auteurCorrection(p, parDefaut) : null,
  }));
}

/** Combien de personnes ont un mot (une remarque non vide). */
export function motsFaits(ps: ParticipantPassation[], remarques: Record<number, string>): number {
  return ps.filter((p) => (remarques[p.participant_id] ?? '').trim() !== '').length;
}

/** La ligne d'information du panneau (pas une tâche) : « 4 personnes débutent » ; le lien « voir la liste » est ajouté par l'écran. */
export const resumeDebutent = (n: number): string =>
  n === 0 ? 'Personne ne débute, d’après Eveil' : `${n} personne${n > 1 ? 's' : ''} débute${n > 1 ? 'nt' : ''}`;

/** Phrase courte du coup d'œil de la remplaçante : « 4 personnes débutent — à accueillir avec soin ». */
export const phraseDebutent = (n: number): { nombre: string; texte: string } =>
  n === 0 ? { nombre: '0', texte: 'personne ne débute dans l’atelier' } : { nombre: String(n), texte: `${n > 1 ? 'personnes débutent' : 'personne débute'} — à accueillir avec soin` };

export const prenom = (nom: string | null | undefined): string => (nom ?? '').trim().split(/\s+/)[0] || '';

/** « d’Alexia » / « de Camille » : l'élision devant une voyelle ou un h (apostrophe typographique, comme les autres textes de l'onglet). */
export const dePrenom = (nom: string | null | undefined): string => (prenom(nom) ? dePersonne(prenom(nom)).replace("'", '’') : 'de la personne');

/** « qu’Alexia » / « que Camille » : « dès qu’Alexia aura transmis… » (élision devant une voyelle ou un h ; apostrophe typographique, comme les autres textes de l'onglet). */
export const quePrenom = (nom: string | null | undefined): string => (prenom(nom) ? queNom(prenom(nom)).replace("'", '’') : 'que la personne');

const JOURS = new Intl.DateTimeFormat('fr-CH', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
const JOURS_LONGS = new Intl.DateTimeFormat('fr-CH', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
const enUtc = (iso: string): Date => new Date(`${iso.slice(0, 10)}T00:00:00Z`);

/** « jeu. 5 nov. » */
export const jourCourt = (iso: string): string => JOURS.format(enUtc(iso)).replace(',', '');
/** « jeudi 5 novembre » */
export const jourLong = (iso: string): string => JOURS_LONGS.format(enUtc(iso)).replace(',', '');
/** « jeudi » */
export const nomDuJour = (iso: string): string => jourLong(iso).split(' ')[0];

/** Ce qu'une reprise a rempli (textes exacts) : pour pouvoir l'annuler sans toucher à ce que la personne a modifié depuis. */
export interface ResumeReprise { date: string; logistique: string | null; remarques: { participant_id: number; remarque: string }[] }

export function planReprise(
  reprise: { logistique_texte: string | null; remarques: { participant_id: number; remarque: string }[] },
  logistiqueActuelle: string, remarquesActuelles: Record<number, string>,
): { logistique: string | null; remarques: { participant_id: number; remarque: string }[] } {
  return {
    // On ne remplit que ce qui est vide : une reprise n'écrase jamais ce que la personne a déjà écrit.
    logistique: reprise.logistique_texte && !logistiqueActuelle.trim() ? reprise.logistique_texte : null,
    remarques: reprise.remarques.filter((r) => !(remarquesActuelles[r.participant_id] ?? '').trim()),
  };
}

/** La passation a été complétée depuis la lecture de la remplaçante (l'enregistrement est plus récent que « lue »). */
export function completeeDepuisLecture(lueAt: string | null | undefined, modifieAt: string | null | undefined): boolean {
  if (!lueAt || !modifieAt) return false;
  return new Date(modifieAt).getTime() > new Date(lueAt).getTime() + 1000;
}
